import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, InputLabel, Select, MenuItem, Typography, Box } from '@mui/material';

export interface MicrophoneSettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

const MicrophoneSettingsDialog: React.FC<MicrophoneSettingsDialogProps> = ({ open, onClose }) => {
  const [devices, setDevices] = React.useState<MediaDeviceInfo[]>([]);
  const [selected, setSelected] = React.useState<string>(() => {
    try { return localStorage.getItem('stt:deviceId') || ''; } catch { return ''; }
  });
  const [error, setError] = React.useState<string | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);

  React.useEffect(() => {
    if (!open) return;
    let mounted = true;
    (async () => {
      try {
        setError(null);
        // Poproś o uprawnienia do mikrofonu, aby uzyskać nazwy urządzeń
        try {
          streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch (e: any) {
          setError('Brak uprawnień do mikrofonu – zezwól w przeglądarce.');
        }
        const list = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = list.filter(d => d.kind === 'audioinput');
        if (mounted) setDevices(audioInputs);
      } catch (e: any) {
        setError(e?.message || 'Nie udało się pobrać listy urządzeń audio.');
      }
    })();
    return () => {
      mounted = false;
      try {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }
      } catch {}
    };
  }, [open]);

  const handleSave = () => {
    try {
      if (selected) localStorage.setItem('stt:deviceId', selected);
      else localStorage.removeItem('stt:deviceId');
      window.dispatchEvent(new CustomEvent('stt:device-changed', { detail: { deviceId: selected || null } }));
    } catch {}
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Wybór mikrofonu</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
        )}
        <FormControl fullWidth>
          <InputLabel id="mic-select-label">Mikrofon</InputLabel>
          <Select
            labelId="mic-select-label"
            label="Mikrofon"
            value={selected}
            onChange={(e) => setSelected(e.target.value as string)}
          >
            <MenuItem value="">
              <em>Domyślne urządzenie systemowe</em>
            </MenuItem>
            {devices.map((d) => (
              <MenuItem key={d.deviceId} value={d.deviceId}>
                {d.label || `Mikrofon (${d.deviceId.slice(0, 6)}…)`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Uwaga: Web Speech API zwykle używa domyślnego urządzenia systemowego.
            Wybranie mikrofonu tutaj postara się go „aktywować”, ale jeśli nie działa, ustaw domyślne urządzenie w ustawieniach systemu.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button variant="contained" onClick={handleSave}>Zapisz</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MicrophoneSettingsDialog;


